import { Link, useParams, Navigate } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import { GkCard, categoryName } from "@/components/feature/ArticleCards";
import { gkArticles } from "@/mocks/news";
import { gkCategories } from "@/mocks/categories";

const SITE = "https://dqxuaad.cn";

export default function NewsDetail() {
  const { id } = useParams();
  const article = gkArticles.find((a) => a.id === id);
  if (!article) return <Navigate to="/" replace />;

  const related = gkArticles.filter((a) => a.category === article.category && a.id !== article.id).slice(0, 3);
  const cat = gkCategories.find((c) => c.slug === article.category);
  const path = `/news/${article.id}`;

  return (
    <>
      <Seo
        title={`${article.title} - 看吃瓜`}
        description={article.summary}
        canonicalPath={path}
        type="article"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "看吃瓜", item: SITE },
              { "@type": "ListItem", position: 2, name: cat?.name, item: `${SITE}/category/${article.category}` },
              { "@type": "ListItem", position: 3, name: article.title, item: SITE + path },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "NewsArticle",
            headline: article.title,
            description: article.summary,
            image: [article.cover],
            datePublished: `${article.date}T${article.time}:00+08:00`,
            dateModified: `${article.date}T${article.time}:00+08:00`,
            author: { "@type": "Person", name: article.author },
            publisher: { "@type": "Organization", name: "看吃瓜", url: SITE },
            mainEntityOfPage: { "@type": "WebPage", "@id": SITE + path },
          },
        ]}
      />

      <div className="mx-auto max-w-7xl px-4 py-8 md:px-8 md:py-10">
        {/* Breadcrumb */}
        <nav aria-label="面包屑" className="mb-6 flex items-center gap-2 text-xs text-foreground-500">
          <Link to="/" className="hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to={`/category/${article.category}`} className="hover:text-primary-600">{categoryName(article.category)}</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="truncate text-foreground-700">{article.title}</span>
        </nav>

        <div className="grid gap-8 lg:grid-cols-3">
          {/* Main article */}
          <article className="lg:col-span-2">
            <div className="rounded-3xl border border-background-200 bg-background-50 p-5 md:p-8">
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <Link to={`/category/${article.category}`} className="rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">{categoryName(article.category)}</Link>
                {article.tags.map((t) => (
                  <span key={t} className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600">{t}</span>
                ))}
              </div>
              <h1 className="font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">{article.title}</h1>
              <p className="mt-4 text-base leading-7 text-foreground-600">{article.summary}</p>
              <div className="mt-6 flex flex-wrap items-center gap-4 border-y border-background-200 py-4 text-xs text-foreground-500">
                <span className="flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent-100 text-accent-700"><i className="ri-user-3-line"></i></span>
                  编辑 · {article.author}
                </span>
                <span className="flex items-center gap-1"><i className="ri-time-line"></i>{article.date} {article.time}</span>
                <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{article.views} 热度</span>
              </div>

              <figure className="mt-6 overflow-hidden rounded-2xl">
                <img src={article.cover} alt={article.title} className="w-full object-cover" />
              </figure>

              <div className="mt-6 space-y-5">
                {article.blocks.map((b, i) => {
                  if (b.type === "h") return <h2 key={i} className="font-heading text-xl font-bold text-foreground-900 md:text-2xl">{b.text}</h2>;
                  if (b.type === "quote")
                    return (
                      <blockquote key={i} className="rounded-2xl border-l-4 border-accent-400 bg-accent-50/60 px-5 py-4 text-base leading-7 text-foreground-700">
                        {b.text}
                      </blockquote>
                    );
                  return <p key={i} className="text-[15px] leading-8 text-foreground-700">{b.text}</p>;
                })}
              </div>

              <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-background-200 pt-6">
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((t) => (
                    <span key={t} className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600"># {t}</span>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-foreground-500">觉得有料？</span>
                  {["ri-weibo-line", "ri-wechat-line", "ri-link-m"].map((icon) => (
                    <button key={icon} type="button" onClick={() => {}} aria-label="分享瓜料" className="flex h-9 w-9 items-center justify-center rounded-full bg-background-100 text-foreground-600 transition-colors hover:bg-primary-500 hover:text-background-50">
                      <i className={icon}></i>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* More in category */}
            <div className="mt-10">
              <div className="mb-6 flex items-center justify-between">
                <h2 className="font-heading text-2xl font-bold text-foreground-950">同栏目还有这些瓜</h2>
                <Link to={`/category/${article.category}`} className="text-sm text-primary-600 hover:underline">去{categoryName(article.category)}瓜田 <i className="ri-arrow-right-line"></i></Link>
              </div>
              <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
                {related.map((a) => (
                  <GkCard key={a.id} article={a} />
                ))}
              </div>
            </div>
          </article>

          {/* Sidebar */}
          <aside className="space-y-6 lg:col-span-1">
            <div className="rounded-3xl border border-background-200 bg-background-50 p-5">
              <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-900"><i className="ri-fire-line text-primary-500"></i>热门瓜料</h2>
              <div className="space-y-3">
                {gkArticles.filter((a) => a.hot).slice(0, 5).map((a, i) => (
                  <Link key={a.id} to={`/news/${a.id}`} className="group flex items-center gap-3">
                    <span className={`font-heading text-xl font-black ${i < 3 ? "text-primary-500" : "text-background-400"}`}>{i + 1}</span>
                    <span className="line-clamp-2 flex-1 text-sm leading-snug text-foreground-800 group-hover:text-primary-600">{a.title}</span>
                  </Link>
                ))}
              </div>
            </div>
            <div className="overflow-hidden rounded-3xl bg-accent-500 p-5 text-background-50 md:p-6">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-background-50/15"><i className="ri-megaphone-line text-lg"></i></span>
              <h3 className="mt-3 font-heading text-xl font-bold">订阅每日瓜料</h3>
              <p className="mt-2 text-sm leading-6 text-background-50/90">每日新瓜、最新爆料与新鲜瓜速递，第一时间端到你的邮箱。</p>
              <Link to="/faq" className="mt-4 inline-block rounded-full bg-background-50 px-5 py-2 text-sm font-medium whitespace-nowrap text-accent-800 hover:bg-background-100">了解订阅方式</Link>
            </div>
            <div className="rounded-3xl border border-background-200 bg-background-50 p-5">
              <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-900"><i className="ri-price-tag-3-line text-primary-500"></i>全部栏目</h2>
              <div className="flex flex-wrap gap-2">
                {gkCategories.map((c) => (
                  <Link key={c.slug} to={`/category/${c.slug}`} className="rounded-full bg-background-100 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:bg-primary-500 hover:text-background-50">{c.name}</Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}